import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import * as Sentry from '@/lib/sentry-lazy';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { clearAdminStudentsCache } from '@/hooks/useAdminStudents';
import { clearSchoolApplicationsCache } from '@/hooks/useSchoolApplications';
import { clearSchoolStudentsCache } from '@/hooks/useSchoolStudents';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import { canRecordIdleLogout } from '@/lib/idleLogout';

// Idle timeout configuration
const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes for normal users
const DEFAULT_IDLE_WARNING_MS = 13 * 60 * 1000; // 2 minutes before timeout

// Special user timeout exceptions
const EXTENDED_TIMEOUT_USERS = {
  'khaggen@nws.k12.mn.us': {
    timeout: 3 * 60 * 60 * 1000, // 3 hours
    warning: (3 * 60 * 60 * 1000) - (2 * 60 * 1000), // 2 minutes before 3 hour timeout
  }
};

// Activity events to track
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'] as const;

interface UserRole {
  role: string;
}

interface UserSchool {
  id: string;
  name: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  userRoles: string[];
  userSchool: UserSchool | null;
  signOut: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
  getRedirectPath: () => string | null;
  showIdleWarning: boolean;
  dismissIdleWarning: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [userSchool, setUserSchool] = useState<UserSchool | null>(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdownSeconds, setIdleCountdownSeconds] = useState(120); // 2 minutes in seconds

  // Idle timeout refs
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get timeout values for current user
  const getTimeoutConfig = useCallback(() => {
    const userEmail = user?.email?.toLowerCase();
    if (userEmail && EXTENDED_TIMEOUT_USERS[userEmail as keyof typeof EXTENDED_TIMEOUT_USERS]) {
      return EXTENDED_TIMEOUT_USERS[userEmail as keyof typeof EXTENDED_TIMEOUT_USERS];
    }
    return {
      timeout: DEFAULT_IDLE_TIMEOUT_MS,
      warning: DEFAULT_IDLE_WARNING_MS,
    };
  }, [user?.email]);

  // Calculate warning countdown in seconds
  const getWarningCountdownSeconds = useCallback(() => {
    const config = getTimeoutConfig();
    return Math.floor((config.timeout - config.warning) / 1000);
  }, [getTimeoutConfig]);

  const fetchUserRoles = async (userId: string) => {
    try {
      // First check regular user roles to prioritize admin access
      let primaryRoles: string[] = [];
      
      // Check standard user roles first
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (rolesError) {
        // Handle JWT expiration and other auth errors
        if (rolesError.message?.includes('JWT') || rolesError.message?.includes('expired')) {
          console.warn('Token expired, attempting refresh...');
          await supabase.auth.refreshSession();
          return; // Will retry on next auth state change
        }
        throw rolesError;
      }
      
      if (rolesData) {
        primaryRoles = rolesData.map((item: UserRole) => item.role);
      }

      setUserRoles(primaryRoles);
      Sentry.setTag('user_role', primaryRoles[0] || 'none');

      // Always check for school assignment regardless of role
      // Some users may be assigned to schools without having explicit school_staff role
      fetchUserSchool(userId);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'fetch user roles' });
      setUserRoles([]);
    } finally {
      // Roles resolved (or failed) — release the auth gate so ProtectedRoute can
      // run its role check against populated userRoles instead of an empty array.
      setIsLoading(false);
    }
  };

  const fetchUserSchool = async (userId: string) => {
    try {
      // Get the school ID from the school_staff table directly
      const { data: staffData, error: staffError } = await supabase
        .from('school_staff')
        .select('school_id')
        .eq('user_id', userId);
      
      if (staffError) {
        // Handle JWT expiration and other auth errors
        if (staffError.message?.includes('JWT') || staffError.message?.includes('expired')) {
          console.warn('Token expired during school fetch, attempting refresh...');
          await supabase.auth.refreshSession();
          return; // Will retry on next auth state change
        }
        console.error('Error fetching staff data:', staffError);
        EnhancedErrorLogger.getInstance().logQuickError(staffError, 'AuthContext', { context: 'fetch staff data' });
        setUserSchool(null);
        return;
      }
      
      if (staffData && staffData.length > 0 && staffData[0].school_id) {
        const schoolId = staffData[0].school_id;
        
        // Then get the school details
        const { data: schoolData, error: schoolError } = await supabase
          .from('schools')
          .select('id, name')
          .eq('id', schoolId)
          .maybeSingle();
          
        if (schoolError) {
          // Handle JWT expiration and other auth errors
          if (schoolError.message?.includes('JWT') || schoolError.message?.includes('expired')) {
            console.warn('Token expired during school details fetch, attempting refresh...');
            await supabase.auth.refreshSession();
            return; // Will retry on next auth state change
          }
          console.error('Error fetching school data:', schoolError);
          EnhancedErrorLogger.getInstance().logQuickError(schoolError, 'AuthContext', { context: 'fetch school data' });
          setUserSchool(null);
          return;
        }
        
        if (schoolData) {
          setUserSchool({
            id: schoolData.id,
            name: schoolData.name
          });
          Sentry.setTag('school_id', schoolData.id);
        }
      } else {
        setUserSchool(null);
      }
    } catch (error) {
      console.error('Error in fetchUserSchool:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'fetch school data' });
      setUserSchool(null);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Keep the realtime websocket bound to the current JWT.
        // Without this, channels keep using the JWT they were created with and
        // throw InvalidJWTToken (Sentry MAGNETFLOW-PORTAL-4) after a long
        // background period when the token has rotated.
        if (session?.access_token) {
          supabase.realtime.setAuth(session.access_token);
        }

        if (session?.user) {
          Sentry.setUser({ id: session.user.id });
          // Defer the supabase query: calling supabase inside onAuthStateChange
          // synchronously deadlocks the auth lock. fetchUserRoles owns clearing
          // isLoading (in its finally) so ProtectedRoute waits for roles and does
          // not bounce authorized users to "/" on a hard refresh / deep link.
          setTimeout(() => fetchUserRoles(session.user.id), 0);
        } else {
          Sentry.setUser(null);
          Sentry.setTag('user_role', undefined);
          Sentry.setTag('school_id', undefined);
          setUserRoles([]);
          setUserSchool(null);
          setIsLoading(false);
        }

        if (!isLoading) {
          if (event === 'SIGNED_IN') {
            // Track successful login with a small delay to ensure session is fully established
            setTimeout(async () => {
              try {
                // Get the current session to ensure it's fully established
                const { data: { session: currentSession } } = await supabase.auth.getSession();

                if (currentSession?.user?.id) {
                  console.log('[AuthContext] Attempting to log login event for user:', currentSession.user.id);
                  const { data, error } = await supabase.from('login_history').insert({
                    user_id: currentSession.user.id,
                    login_time: new Date().toISOString(),
                    ip_address: window.location.hostname,
                    user_agent: navigator.userAgent,
                    login_method: 'password', // Could be enhanced to track OAuth vs password
                    success: true
                  }).select();

                  if (error) {
                    console.error('[AuthContext] Failed to log login event:', error);
                    EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log login event' });
                  } else {
                    console.log('[AuthContext] Login event logged successfully:', data);
                  }
                }
              } catch (error) {
                console.error('[AuthContext] Exception while logging login event:', error);
                EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log login exception' });
              }
            }, 500); // 500ms delay to ensure session is established
          } else if (event === 'SIGNED_OUT') {
            // Track logout
            if (user?.id) {
              try {
                console.log('[AuthContext] Attempting to log logout event for user:', user.id);
                const { data, error } = await supabase.from('login_history').insert({
                  user_id: user.id,
                  login_time: new Date().toISOString(),
                  ip_address: window.location.hostname,
                  user_agent: navigator.userAgent,
                  login_method: 'logout',
                  success: true
                }).select();

                if (error) {
                  console.error('[AuthContext] Failed to log logout event:', error);
                  EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log logout event' });
                } else {
                  console.log('[AuthContext] Logout event logged successfully:', data);
                }
              } catch (error) {
                console.error('[AuthContext] Exception while logging logout event:', error);
                EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log logout exception' });
              }
            }
          }
        }

        // Note: do NOT clear isLoading here for the signed-in case — fetchUserRoles
        // clears it once roles resolve (see its finally block).
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.access_token) {
        supabase.realtime.setAuth(session.access_token);
      }

      if (session?.user) {
        // Safe to call directly here (not inside onAuthStateChange).
        // fetchUserRoles clears isLoading in its finally.
        fetchUserRoles(session.user.id);
      } else {
        setIsLoading(false);
      }
    }).catch(err => {
      console.error('Failed to get session:', err);
      EnhancedErrorLogger.getInstance().logQuickError(err, 'AuthContext', { context: 'get session' });
    });

    return () => subscription.unsubscribe();
  }, []);

  // Idle timeout logout functionality
  const handleIdleLogout = useCallback(async () => {
    if (!user) return;

    console.log('[AuthContext] Idle timeout reached, logging out user');
    toast.warning('You have been logged out due to inactivity');

    try {
      // Log the idle logout, but only while the session is still valid: after the idle timeout the JWT has
      // usually expired, the insert would run as anon and RLS rejects it (Sentry 7776517040).
      const { data: sessionData } = await supabase.auth.getSession();
      if (canRecordIdleLogout(sessionData?.session, Date.now())) {
        const { error: idleLogoutInsertErr } = await supabase.from('login_history').insert({
          user_id: user.id,
          login_time: new Date().toISOString(),
          ip_address: window.location.hostname,
          user_agent: navigator.userAgent,
          login_method: 'idle_logout',
          success: true
        });
        if (idleLogoutInsertErr) {
          console.error('Failed to insert login history:', idleLogoutInsertErr);
          EnhancedErrorLogger.getInstance().logQuickError(idleLogoutInsertErr, 'AuthContext', { context: 'insert login history' });
        }
      }
    } catch (error) {
      console.error('[AuthContext] Failed to log idle logout:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log idle logout' });
    }

    // Perform sign out
    await signOut();
  }, [user]);

  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowIdleWarning(false);

    // Clear existing timeouts
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }

    // Only set timers if user is logged in
    if (!user) return;

    // Get timeout config for this user
    const config = getTimeoutConfig();
    const warningCountdown = getWarningCountdownSeconds();

    // Set warning timeout (2 minutes before logout)
    warningTimeoutRef.current = setTimeout(() => {
      setShowIdleWarning(true);
      setIdleCountdownSeconds(warningCountdown); // Reset countdown
    }, config.warning);

    // Set logout timeout
    idleTimeoutRef.current = setTimeout(() => {
      handleIdleLogout();
    }, config.timeout);
  }, [user, handleIdleLogout, getTimeoutConfig, getWarningCountdownSeconds]);

  // Set up idle tracking when user is logged in
  useEffect(() => {
    if (!user) {
      // Clear timers when logged out
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      return;
    }

    // Throttled activity handler to prevent excessive calls
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const handleActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        resetIdleTimer();
      }, 1000); // Throttle to once per second
    };

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility changes - reset timer when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - check if we should have logged out
        const config = getTimeoutConfig();
        const timeSinceActivity = Date.now() - lastActivityRef.current;
        if (timeSinceActivity >= config.timeout) {
          handleIdleLogout();
        } else {
          resetIdleTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the idle timer
    resetIdleTimer();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (throttleTimer) clearTimeout(throttleTimer);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    };
  }, [user, resetIdleTimer, handleIdleLogout]);

  const signOut = useCallback(async () => {
    try {
      // Track logout before clearing session
      if (user?.id) {
        try {
          console.log('[AuthContext] Manual logout - attempting to log event for user:', user.id);
          const { data, error } = await supabase.from('login_history').insert({
            user_id: user.id,
            login_time: new Date().toISOString(),
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
            login_method: 'manual_logout',
            success: true
          }).select();

          if (error) {
            console.error('[AuthContext] Failed to log manual logout event:', error);
            EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log manual logout' });
          } else {
            console.log('[AuthContext] Manual logout event logged successfully:', data);
          }
        } catch (error) {
          console.error('[AuthContext] Exception while logging manual logout event:', error);
          EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'log manual logout exception' });
        }
      }

      // Clear all local storage first
      localStorage.clear();
      sessionStorage.clear();

      // Clear in-memory caches to prevent stale data on re-login
      try {
        clearAdminStudentsCache();
        clearSchoolApplicationsCache();
        clearSchoolStudentsCache();
        console.log('[AuthContext] Cleared all in-memory caches');
      } catch (cacheError) {
        console.warn('[AuthContext] Error clearing caches:', cacheError);
      }

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear context state
      setSession(null);
      setUser(null);
      setUserRoles([]);
      setUserSchool(null);
      setShowIdleWarning(false);

      // Clear any Supabase session data
      await supabase.auth.refreshSession();

      // Leave via a FULL page load rather than a client-side route change.
      // Logging out is a hard boundary, and the redirect to /auth is the first
      // thing that needs a lazily-loaded chunk — so after a deploy has replaced
      // the hashed assets, this is exactly where an open tab 404s and strands
      // the user on "Page Refresh Required" instead of showing a login page.
      // A full load fetches the current index.html and its current hashes.
      if (typeof window !== 'undefined') {
        window.location.assign('/auth');
        return;
      }

    } catch (error) {
      console.error('[AuthContext] Sign out error:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AuthContext', { context: 'sign out' });
      toast.error('Failed to sign out. Please try again.');
      throw error; // Re-throw to handle in the component
    }
  }, [user?.id]);

  const hasRole = useCallback((role: string | string[]): boolean => {
    if (!user || !userRoles) return false;

    if (Array.isArray(role)) {
      return role.some(r => userRoles.includes(r));
    }

    return userRoles.includes(role);
  }, [user, userRoles]);

  const getRedirectPath = useCallback((): string | null => {
    if (!user) return null;

    // Prioritize admin roles
    if (userRoles.includes('admin')) {
      return '/admin';
    }

    if (userRoles.includes('district_admin')) {
      return '/district-admin';
    }

    if (userRoles.includes('district_partner')) {
      return '/partner';
    }

    if (userRoles.includes('school_staff')) {
      return '/school';
    }

    return null;
  }, [user, userRoles]);

  // Dismiss idle warning and reset timer
  const dismissIdleWarning = useCallback(() => {
    setShowIdleWarning(false);
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Countdown timer and keyboard shortcuts for idle warning
  useEffect(() => {
    if (!showIdleWarning || !user) {
      // Clear countdown interval when warning is not shown
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setIdleCountdownSeconds(prev => {
        if (prev <= 1) {
          // Countdown reached 0, logout will be triggered by the timeout
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Keyboard shortcuts handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // ESC dismisses warning and resets timer
        dismissIdleWarning();
      } else if (e.key === 'Enter') {
        // Enter keeps user logged in
        dismissIdleWarning();
      }
    };

    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showIdleWarning, user, dismissIdleWarning]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    session,
    user,
    isLoading,
    userRoles,
    userSchool,
    signOut,
    hasRole,
    getRedirectPath,
    showIdleWarning,
    dismissIdleWarning,
  }), [session, user, isLoading, userRoles, userSchool, signOut, hasRole, getRedirectPath, showIdleWarning, dismissIdleWarning]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Idle Warning Modal - only show when user is logged in */}
      {showIdleWarning && user && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-nwsisd-soft-cream rounded-lg shadow-2xl p-6 max-w-md mx-4 animate-in fade-in zoom-in duration-200 border border-nwsisd-light-blue-gray">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-nwsisd-light-blue-gray/30 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-nwsisd-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-nwsisd-dark-blue mb-2">Session Timeout Warning</h2>
              <p className="text-nwsisd-dark-blue/70 mb-2">
                You will be logged out in{' '}
                <span className="font-bold text-nwsisd-teal text-2xl tabular-nums">
                  {(() => {
                    const hours = Math.floor(idleCountdownSeconds / 3600);
                    const minutes = Math.floor((idleCountdownSeconds % 3600) / 60);
                    const seconds = idleCountdownSeconds % 60;

                    if (hours > 0) {
                      // Show HH:MM:SS format for extended timeout users
                      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                    } else {
                      // Show MM:SS format for normal users
                      return `${minutes}:${String(seconds).padStart(2, '0')}`;
                    }
                  })()}
                </span>
                {' '}due to inactivity.
              </p>
              <p className="text-xs text-nwsisd-dark-blue/50 mb-6">
                Press <kbd className="px-1.5 py-0.5 bg-nwsisd-light-blue-gray/30 rounded text-nwsisd-dark-blue font-mono">ESC</kbd> or{' '}
                <kbd className="px-1.5 py-0.5 bg-nwsisd-light-blue-gray/30 rounded text-nwsisd-dark-blue font-mono">Enter</kbd> to stay logged in
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={dismissIdleWarning}
                  className="flex-1 px-4 py-2.5 bg-nwsisd-dark-blue text-white rounded-lg font-medium hover:bg-nwsisd-dark-blue/90 transition-colors focus:outline-none focus:ring-2 focus:ring-nwsisd-teal focus:ring-offset-2"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={() => signOut()}
                  className="flex-1 px-4 py-2.5 bg-nwsisd-soft-beige/50 text-nwsisd-dark-blue rounded-lg font-medium hover:bg-nwsisd-soft-beige transition-colors focus:outline-none focus:ring-2 focus:ring-nwsisd-dark-blue focus:ring-offset-2"
                >
                  Log Out Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
